import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  NO_SCREEN,
  TILL_COLOURS,
  TILL_COLOUR_LABELS_FR,
  TILL_ROLE_LABELS_FR,
  tillCategoriesFor,
  tillDraftProblem,
  tillFormatEuros,
  tillParseEuros,
  tillScreens,
  type TillCarte,
  type TillColour,
  type TillProduct,
  type TillProductDraft,
  type TillRole,
} from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { usePms } from './PmsProvider.js';

// « Carte » — what the bar and the restaurant sell (docs/PMS-TILL-BRIEF.md §16).
//
// In the RESTAURANT group of both cockpits, beside « Menus » (operator, 2026-10-19), not in PMS › Configuration
// where the stay's extras sit: the carte is the salle's, the extras are the stay's. Editable in both cockpits under
// `pms.catalogue.write` (the desk holds it since 20261019230100).
//
// PRODUCTS ONLY (C-Q1). A formule's questions and components are SHOWN — the page says what each product asks,
// contains or is a choice of — but not edited here: the structure changes rarely and is where a mistake breaks
// ringing, so it gets its own step when the first change is needed.
//
// What a change does is said where it is made: a new price is for lines rung AFTER it; a ticket keeps the price it
// was sold at (T2-Q3, edited in place).

interface CarteForm {
  id: number | null;
  role: TillRole;
  labelFr: string;
  priceEuros: string;
  categoryId: number | null;
  screen: string;
  colour: TillColour | '';
  sortOrder: string;
}

const emptyForm = (): CarteForm => ({
  id: null, role: 'item', labelFr: '', priceEuros: '', categoryId: null, screen: '', colour: '', sortOrder: '100',
});

const toForm = (p: TillProduct): CarteForm => ({
  id: p.id,
  role: p.role,
  labelFr: p.labelFr,
  priceEuros: p.role === 'open_price' ? '' : tillFormatEuros(p.priceCents),
  categoryId: p.categoryId,
  screen: p.screen ?? '',
  colour: p.colour ?? '',
  sortOrder: String(p.sortOrder),
});

/** The form → a draft, or the sentence that says why not. An existing product keeps its own role (a formule stays
 *  a formule, and may cost 0 € when its components make the price). */
function buildDraft(form: CarteForm, editing: TillProduct | null): { ok: true; draft: TillProductDraft } | { ok: false; error: string } {
  const price = form.role === 'open_price' ? 0 : tillParseEuros(form.priceEuros);
  if (price === null) return { ok: false, error: 'Le prix est un montant en euros, par exemple 4,50.' };
  if (form.categoryId === null) return { ok: false, error: 'Choisissez une catégorie.' };
  const order = Number.parseInt(form.sortOrder, 10);
  return {
    ok: true,
    draft: {
      ...(form.id === null ? {} : { id: form.id }),
      role: editing?.role ?? form.role,
      labelFr: form.labelFr,
      priceCents: price,
      categoryId: form.categoryId,
      screen: form.screen.trim() || null,
      colour: form.colour === '' ? null : form.colour,
      sortOrder: Number.isFinite(order) ? order : 100,
    },
  };
}

/** What the structure says about a product, in one line. */
function structureLine(p: TillProduct): string | null {
  const parts: string[] = [];
  if (p.asks.length > 0) parts.push(`demande : ${p.asks.join(', ')}`);
  if (p.contains.length > 0) {
    parts.push(`contient : ${p.contains.map((c) => (c.required ? c.labelFr : `${c.labelFr} (en option)`)).join(', ')}`);
  }
  if (p.choiceOf.length > 0) parts.push(`au choix dans : ${p.choiceOf.join(', ')}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function Carte() {
  const pms = usePms();
  const canEdit = useCapability(CAPABILITIES.PMS_CATALOGUE_WRITE);
  const [carte, setCarte] = useState<TillCarte | null>(null);
  const [form, setForm] = useState<CarteForm | null>(null);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const [showRetired, setShowRetired] = useState(false);

  const load = useCallback(async () => {
    const r = await pms.tillCatalogue.carte();
    if (!r.ok) {
      setErr(r.error.message);
      return;
    }
    setCarte(r.value);
  }, [pms]);

  useEffect(() => {
    void load();
  }, [load]);

  const editing = useMemo(
    () => (form?.id != null ? (carte?.products.find((p) => p.id === form.id) ?? null) : null),
    [form, carte],
  );

  const screens = useMemo(() => {
    const q = filter.trim().toLocaleLowerCase('fr');
    const shown = (carte?.products ?? []).filter(
      (p) => (showRetired || p.active) && (q === '' || p.labelFr.toLocaleLowerCase('fr').includes(q)),
    );
    return tillScreens(shown);
  }, [carte, filter, showRetired]);

  const knownScreens = useMemo(
    () => [...new Set((carte?.products ?? []).map((p) => p.screen).filter((s): s is string => s !== null))].sort(),
    [carte],
  );

  const category = (id: number) => carte?.categories.find((c) => c.id === id);

  const save = async () => {
    if (form === null || carte === null) return;
    const built = buildDraft(form, editing);
    if (!built.ok) {
      setErr(built.error);
      return;
    }
    const problem = tillDraftProblem(built.draft, carte);
    if (problem !== null) {
      setErr(problem);
      return;
    }
    setSaving(true);
    setErr('');
    const r = await pms.tillCatalogue.saveTillProduct(built.draft);
    setSaving(false);
    if (!r.ok) {
      setErr(r.error.message);
      return;
    }
    const priceMoved = editing !== null && editing.priceCents !== built.draft.priceCents;
    setNotice(
      priceMoved
        ? `« ${built.draft.labelFr.trim()} » : nouveau prix enregistré. Il vaut pour les lignes encaissées à partir de maintenant ; une addition déjà ouverte garde le prix de ses lignes.`
        : `« ${built.draft.labelFr.trim()} » enregistré.`,
    );
    setForm(null);
    await load();
  };

  const toggleActive = async (p: TillProduct) => {
    // Never a delete: a note line and its ticket name the product.
    if (
      p.active &&
      !window.confirm(`Retirer « ${p.labelFr} » de la carte ?\n\nIl ne sera plus proposé à la caisse. Les tickets passés le montrent toujours.`)
    ) {
      return;
    }
    setErr('');
    setNotice('');
    const r = await pms.tillCatalogue.setTillProductActive(p.id, !p.active);
    if (!r.ok) setErr(r.error.message);
    await load();
  };

  const total = carte?.products.length ?? 0;
  const active = carte?.products.filter((p) => p.active).length ?? 0;

  return (
    <div className="pms-catalogue pms-carte">
      <div className="pms-head">
        <h2 className="section-title">Carte</h2>
        <span className="pms-hint">
          Ce que le bar et le restaurant vendent à la caisse.{' '}
          {canEdit ? 'Cliquez ✎ pour modifier un produit.' : 'Lecture seule.'}
        </span>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {notice && <p className="pms-hint" role="status">{notice}</p>}

      {carte === null && !err && <p className="pms-empty">Chargement…</p>}

      {carte !== null && total === 0 && (
        <p className="pms-empty">Pas de carte pour cet établissement : seul l’hôtel a un bar et un restaurant.</p>
      )}

      {carte !== null && total > 0 && (
        <div className="pms-carte-tools">
          <input
            type="search"
            aria-label="Chercher un produit"
            placeholder="Chercher un produit"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <label className="pms-carte-check">
            <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
            Afficher les produits retirés
          </label>
          <span className="pms-hint">
            {active} produits à la carte{total > active ? ` · ${total - active} retirés` : ''}
          </span>
          {canEdit && form === null && (
            <button type="button" className="auth-btn is-ghost" onClick={() => { setNotice(''); setForm(emptyForm()); }}>
              + Ajouter un produit
            </button>
          )}
        </div>
      )}

      {form !== null && carte !== null && (
        <CarteFields
          form={form}
          editing={editing}
          carte={carte}
          knownScreens={knownScreens}
          saving={saving}
          onChange={(patch) => setForm((f) => (f === null ? f : { ...f, ...patch }))}
          onSave={() => void save()}
          onCancel={() => {
            setForm(null);
            setErr('');
          }}
        />
      )}

      {carte !== null && total > 0 && screens.length === 0 && (
        <p className="pms-empty">Aucun produit ne correspond à « {filter} ».</p>
      )}

      {screens.map(({ screen, products }) => (
        <section key={screen} className="pms-carte-screen">
          <h3>{screen === NO_SCREEN ? 'Sans écran à la caisse' : screen}</h3>
          <table className="pms-table">
            <thead>
              <tr>
                <th>Produit</th>
                <th className="pms-num">Prix</th>
                <th>Catégorie</th>
                <th>État</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const c = category(p.categoryId);
                const line = structureLine(p);
                return (
                  <tr key={p.id} className={p.active ? '' : 'is-inactive'}>
                    <td>
                      {p.colour && <span className={`pms-carte-swatch is-${p.colour}`} aria-hidden="true" />}
                      {p.labelFr}
                      {p.role !== 'item' && <span className="pms-carte-role">{TILL_ROLE_LABELS_FR[p.role]}</span>}
                      {line && <div className="pms-sub">{line}</div>}
                    </td>
                    <td className="pms-num">
                      {p.role === 'open_price'
                        ? 'saisi à la caisse'
                        : p.role === 'option'
                          ? p.priceCents === 0 ? 'inclus' : `+ ${tillFormatEuros(p.priceCents)} €`
                          : `${tillFormatEuros(p.priceCents)} €`}
                    </td>
                    <td>{c ? `${c.code} · ${String(c.tvaRate).replace('.', ',')} %` : '—'}</td>
                    <td>{p.active ? 'À la carte' : 'Retiré'}</td>
                    <td className="pms-actions">
                      {canEdit && (
                        <>
                          <button
                            type="button"
                            className="pms-mini"
                            aria-label={`Modifier ${p.labelFr}`}
                            onClick={() => { setNotice(''); setForm(toForm(p)); }}
                          >
                            ✎
                          </button>
                          <button type="button" className="pms-mini" onClick={() => void toggleActive(p)}>
                            {p.active ? 'Retirer' : 'Remettre'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

interface CarteFieldsProps {
  form: CarteForm;
  editing: TillProduct | null;
  carte: TillCarte;
  knownScreens: string[];
  saving: boolean;
  onChange: (patch: Partial<CarteForm>) => void;
  onSave: () => void;
  onCancel: () => void;
}

function CarteFields({ form, editing, carte, knownScreens, saving, onChange, onSave, onCancel }: CarteFieldsProps) {
  const categories = tillCategoriesFor(editing, carte);
  const role: TillRole = editing?.role ?? form.role;
  const line = editing ? structureLine(editing) : null;
  return (
    <section className="pms-form">
      <h3>{form.id === null ? 'Nouveau produit' : `Modifier « ${editing?.labelFr ?? form.labelFr} »`}</h3>
      {line && (
        <p className="pms-hint">
          {line}. La composition des formules ne se modifie pas ici.
        </p>
      )}
      <div className="pms-form-grid">
        <label className="pms-field">
          <span>Type</span>
          {/* Fixed once created: changing it would break the formule it sits in (C-Q1). */}
          <select
            value={role}
            disabled={form.id !== null}
            onChange={(e) => onChange({ role: e.target.value as TillRole, priceEuros: e.target.value === 'open_price' ? '' : form.priceEuros })}
          >
            {(form.id === null ? (['item', 'option', 'open_price'] as const) : ([role] as const)).map((r) => (
              <option key={r} value={r}>{TILL_ROLE_LABELS_FR[r]}</option>
            ))}
          </select>
        </label>
        <label className="pms-field pms-field-wide">
          <span>Libellé *</span>
          <input value={form.labelFr} placeholder="Spritz" onChange={(e) => onChange({ labelFr: e.target.value })} />
        </label>
        <label className="pms-field">
          <span>{role === 'option' ? 'Supplément (€)' : 'Prix (€)'}{role === 'open_price' ? '' : ' *'}</span>
          <input
            inputMode="decimal"
            placeholder={role === 'open_price' ? 'saisi à la caisse' : '8,00'}
            disabled={role === 'open_price'}
            value={form.priceEuros}
            onChange={(e) => onChange({ priceEuros: e.target.value })}
          />
        </label>
        <label className="pms-field">
          <span>Catégorie *</span>
          <select
            value={form.categoryId ?? ''}
            onChange={(e) => onChange({ categoryId: e.target.value === '' ? null : Number(e.target.value) })}
          >
            <option value="">—</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} · {c.labelFr} ({String(c.tvaRate).replace('.', ',')} %)
              </option>
            ))}
          </select>
        </label>
        {role !== 'option' && (
          <>
            <label className="pms-field pms-field-wide">
              <span>Écran de la caisse</span>
              <input
                list="pms-carte-screens"
                value={form.screen}
                placeholder="Bar / Apéritifs & Alcools"
                onChange={(e) => onChange({ screen: e.target.value })}
              />
              <datalist id="pms-carte-screens">
                {knownScreens.map((s) => <option key={s} value={s} />)}
              </datalist>
            </label>
            <label className="pms-field">
              <span>Couleur du bouton</span>
              <select value={form.colour} onChange={(e) => onChange({ colour: e.target.value as TillColour | '' })}>
                <option value="">—</option>
                {TILL_COLOURS.map((c) => <option key={c} value={c}>{TILL_COLOUR_LABELS_FR[c]}</option>)}
              </select>
            </label>
          </>
        )}
        <label className="pms-field">
          <span>Ordre</span>
          <input inputMode="numeric" value={form.sortOrder} onChange={(e) => onChange({ sortOrder: e.target.value })} />
        </label>
      </div>
      {editing !== null && role !== 'open_price' && (
        <p className="pms-hint">Un nouveau prix vaut pour les lignes encaissées après l’enregistrement ; un ticket garde le prix auquel il a été vendu.</p>
      )}
      <div className="pms-form-actions">
        <button type="button" className="auth-btn" disabled={saving} onClick={onSave}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button type="button" className="auth-btn is-ghost" onClick={onCancel}>
          Annuler
        </button>
      </div>
    </section>
  );
}
